// Listing modes for Pilot Roster — Month / List / My Roster.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/ads-and-sbs/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/month   → PilotRosterMonth (month x pilot grid, the legacy
//                                       scheduler's default view)
//   <base>/list    → PilotRosterGrid  (flat AeronetDataGrid)
//   <base>/my-roster → PilotRosterMyRoster (the signed-in user's own days,
//                                       four months — H295, the full-app twin
//                                       of mobile's My Roster)
//
// NO day-board mode: aircraft_roster has one because its legacy screen shipped
// a day lens; mod_pilot_roster did not. A mode is added when the legacy screen
// had it, not for symmetry with a sibling module.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  month: "Month",
  list: "List",
  "my-roster": "My Roster",
  // G6 (flight admin point 9): the qualification matrix — a matrix view is a
  // top-level mode (CLAUDE.md), not a block inside another screen.
  qualifications: "Qualifications",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  month: "Duty days per person",
  list: "Every duty entry",
  "my-roster": "Your duty, 4 months",
  qualifications: "Aircraft and work ratings",
};

/**
 * Month, not List. The legacy dhtmlxScheduler opens on its MONTH / timeline
 * view — pilots down the side grouped by roster type, the whole month across
 * the top — and that IS the surface an operator opens this module for. List is
 * the flat-table alternative.
 */
export const DEFAULT_MODE_SEGMENT = "month";

/**
 * `<module>.viewMode` — same convention as every other converted module.
 * Nothing has ever written this key (the module has no v6 predecessor), so
 * there is no stored value to migrate; it exists so a bare module root can
 * restore the last mode from here on.
 */
export const MODE_STORAGE_KEY = "pilot_roster.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NOTHING TO MIGRATE: this module ships its mode routes from day one, so no
 * value predates the segments below.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
